import React, { useCallback, useEffect, useMemo, useState } from "react";

import { FlaskConical, Loader2, Play, Save, Trash2 } from "lucide-react";

import Button from "../../Components/Button";
import DropdownSelector from "../../Components/DropdownSelector";
import CommonInput from "../../Components/Input";
import CommonModal from "../../Components/Modal";
import ToggleSwitch from "../../Components/ToggleSwitch";
import { useAppDispatch } from "../../Store/Hooks";
import {
  fetchAlAssessmentTestMode,
  resetAlAssessmentTestModeData,
  runAlAssessmentTestModeJob,
  updateAlAssessmentTestMode,
  type AlAssessmentTestModeLastRun,
  type AlAssessmentTestModeOrg,
} from "../../Store/Reducers/AdminSlice";
import { toast } from "../../Utils/Helper/toast";

import "./style.scss";

const SELECTED_ORG_KEY = "al-assessment-test-schema";

const errorMessage = (error: unknown, fallback: string): string =>
  typeof error === "string" && error ? error : fallback;

const rememberedSchema = (): string => {
  try {
    return localStorage.getItem(SELECTED_ORG_KEY) || "";
  } catch {
    return "";
  }
};

const rememberSchema = (schema: string): void => {
  try {
    if (schema) localStorage.setItem(SELECTED_ORG_KEY, schema);
  } catch {
    /* ignore quota / private mode */
  }
};

const pickSchema = (orgs: AlAssessmentTestModeOrg[]): string => {
  const stored = rememberedSchema();
  const enabledOrgs = orgs.filter(org => org.enabled);
  if (stored && enabledOrgs.some(org => org.schema_name === stored)) {
    return stored;
  }
  if (enabledOrgs[0]) return enabledOrgs[0].schema_name;
  if (stored && orgs.some(org => org.schema_name === stored)) return stored;
  return orgs[0]?.schema_name || "";
};

const formatLastRun = (
  lastRun?: AlAssessmentTestModeLastRun | null
): string => {
  if (!lastRun) return "No job has been run for this organization yet.";
  const when = lastRun.at ? lastRun.at.replace("T", " ").slice(0, 19) : "—";
  const asOf = lastRun.as_of || "—";
  const who = lastRun.by || "unknown";
  const status = lastRun.ok ? "ok" : "failed";
  return `${status} · as-of ${asOf} · ${who} · ${when}${
    lastRun.summary ? ` · ${lastRun.summary}` : ""
  }`;
};

const AdminAlAssessmentTestContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [realToday, setRealToday] = useState("");
  const [orgs, setOrgs] = useState<AlAssessmentTestModeOrg[]>([]);
  const [schemaName, setSchemaName] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [asOf, setAsOf] = useState("");
  const [enableConfirmOpen, setEnableConfirmOpen] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  const selectedOrg = useMemo(
    () => orgs.find(org => org.schema_name === schemaName) || null,
    [orgs, schemaName]
  );

  const applyOrg = useCallback(
    (org: AlAssessmentTestModeOrg | null, today: string) => {
      if (!org) {
        setEnabled(false);
        setAsOf(today);
        return;
      }
      setEnabled(Boolean(org.enabled));
      setAsOf(org.as_of || today);
    },
    []
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const content = await dispatch(fetchAlAssessmentTestMode()).unwrap();
      const nextOrgs = content.orgs || [];
      setRealToday(content.real_today);
      setOrgs(nextOrgs);
      const nextSchema = pickSchema(nextOrgs);
      setSchemaName(nextSchema);
      rememberSchema(nextSchema);
      applyOrg(
        nextOrgs.find(org => org.schema_name === nextSchema) || null,
        content.real_today
      );
    } catch (error) {
      toast.error(
        errorMessage(error, "Failed to load AL assessment test mode.")
      );
    } finally {
      setLoading(false);
    }
  }, [applyOrg, dispatch]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSelectOrg = (value: string) => {
    setSchemaName(value);
    rememberSchema(value);
    const org = orgs.find(item => item.schema_name === value) || null;
    applyOrg(org, realToday);
  };

  const persist = async (
    nextSchema: string,
    nextEnabled: boolean,
    nextAsOf: string
  ) => {
    if (!nextSchema) return;
    const asOfValue = nextAsOf || realToday;
    setSaving(true);
    try {
      const content = await dispatch(
        updateAlAssessmentTestMode({
          schema_name: nextSchema,
          enabled: nextEnabled,
          as_of: asOfValue || null,
        })
      ).unwrap();
      if (content.real_today) setRealToday(content.real_today);
      if (content.org) {
        rememberSchema(content.org.schema_name);
        setSchemaName(content.org.schema_name);
        setOrgs(prev => {
          const exists = prev.some(
            org => org.schema_name === content.org.schema_name
          );
          return exists
            ? prev.map(org =>
                org.schema_name === content.org.schema_name ? content.org : org
              )
            : [...prev, content.org];
        });
        applyOrg(content.org, content.real_today);
      }
      toast.success("Test Mode saved.");
    } catch (error) {
      toast.error(errorMessage(error, "Failed to save Test Mode."));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (next: boolean) => {
    if (next && !enabled) {
      setEnableConfirmOpen(true);
      return;
    }
    setEnabled(next);
    if (!next) {
      void persist(schemaName, false, asOf);
    }
  };

  const handleConfirmEnable = () => {
    const nextAsOf = asOf || realToday;
    setEnabled(true);
    setAsOf(nextAsOf);
    setEnableConfirmOpen(false);
    void persist(schemaName, true, nextAsOf);
  };

  const handleRun = async () => {
    if (!schemaName) return;
    setRunning(true);
    try {
      const content = (await dispatch(
        runAlAssessmentTestModeJob({ schema_name: schemaName, dry_run: false })
      ).unwrap()) as {
        org?: AlAssessmentTestModeOrg;
        job?: { summary?: string };
      };
      if (content?.org) {
        setOrgs(prev =>
          prev.map(org =>
            org.schema_name === content.org?.schema_name ? content.org : org
          )
        );
      }
      toast.success(content?.job?.summary || "Daily job finished.");
    } catch (error) {
      toast.error(errorMessage(error, "Failed to run the daily AL job."));
    } finally {
      setRunning(false);
    }
  };

  const handleReset = async () => {
    if (!schemaName) return;
    setResetting(true);
    try {
      await dispatch(
        resetAlAssessmentTestModeData({ schema_name: schemaName })
      ).unwrap();
      setResetConfirmOpen(false);
      toast.success("AL assessment data reset for this organization.");
    } catch (error) {
      toast.error(errorMessage(error, "Failed to reset AL assessment data."));
    } finally {
      setResetting(false);
    }
  };

  const orgOptions = orgs.map(org => ({
    label: org.enabled ? `${org.name} · Test mode on` : org.name,
    value: org.schema_name,
  }));

  return (
    <div className="admin-al-test">
      <div className="admin-al-test__header">
        <div className="admin-al-test__icon">
          <FlaskConical size={20} />
        </div>
        <div>
          <h2 className="admin-al-test__title">AL Assessment Test</h2>
          <p className="admin-al-test__subtitle">
            Turn Test Mode on for one organization, set Test as-of, then run the
            daily AL job. Other organizations keep the real date.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex h-10 items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin text-violet-600" />
          Loading…
        </div>
      ) : (
        <div className="admin-al-test__card">
          <div className="admin-al-test__org">
            <DropdownSelector
              label="Organization"
              selected={schemaName || null}
              options={orgOptions}
              onSelect={handleSelectOrg}
              triggerStyle="field"
              fullWidth
              enableScroll
            />
            {selectedOrg?.enabled ? (
              <span className="admin-al-test__chip">Test mode on</span>
            ) : null}
          </div>

          <div className="admin-al-test__toggle">
            <div className="admin-al-test__toggle-copy">
              <span className="admin-al-test__label">Test mode</span>
              <p className="admin-al-test__hint">
                On: this org&apos;s AL dates use Test as-of. Off: real today.
                Turning off does not rewrite saved rows.
              </p>
            </div>
            <ToggleSwitch
              checked={enabled}
              onChange={handleToggle}
              id="al-assessment-test-mode"
            />
          </div>

          <div className="admin-al-test__row">
            <CommonInput
              variant="field"
              label="Real today (UTC)"
              name="real_today"
              type="date"
              value={realToday}
              onChange={() => undefined}
              readOnly
              disabled
            />
            <CommonInput
              variant="field"
              label="Test as-of"
              name="as_of"
              type="date"
              value={asOf}
              onChange={event => setAsOf(event.target.value)}
              disabled={!enabled}
            />
          </div>
          <p className="admin-al-test__hint">
            Changing Test as-of does not open windows or publish. Save, then
            click Run daily job now on an open day or due + 1.
          </p>

          <div className="admin-al-test__actions">
            <Button
              variant="primary"
              onClick={() => void persist(schemaName, enabled, asOf)}
              disabled={saving || !schemaName}
              loading={saving}
              icon={<Save className="h-4 w-4" />}
            >
              Save
            </Button>
            <Button
              variant="outline-primary"
              onClick={() => void handleRun()}
              disabled={running || !enabled || !schemaName}
              loading={running}
              icon={<Play className="h-4 w-4" />}
            >
              Run daily job now
            </Button>
            <Button
              variant="danger"
              onClick={() => setResetConfirmOpen(true)}
              disabled={resetting || !schemaName}
              icon={<Trash2 className="h-4 w-4" />}
            >
              Reset AL data
            </Button>
          </div>

          <div className="admin-al-test__banner">
            <strong>Last run.</strong> {formatLastRun(selectedOrg?.last_run)}
          </div>
          {!enabled ? (
            <div className="admin-al-test__warn">
              Future-dated AL rows stay until you Reset. Use a test org.
            </div>
          ) : null}
        </div>
      )}

      <CommonModal
        isOpen={enableConfirmOpen}
        toggle={() => setEnableConfirmOpen(false)}
        title="Turn Test Mode on?"
        footer={
          <div className="flex w-full items-center justify-end gap-2">
            <Button variant="white" onClick={() => setEnableConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmEnable}
              disabled={saving || !schemaName}
              loading={saving}
            >
              Turn on
            </Button>
          </div>
        }
      >
        <p className="mb-0 text-sm text-gray-700">
          This org&apos;s AL dates will move ahead of real today. Use a test
          org, or Reset before turning off.
        </p>
      </CommonModal>

      <CommonModal
        isOpen={resetConfirmOpen}
        toggle={() => !resetting && setResetConfirmOpen(false)}
        title="Reset AL data?"
        footer={
          <div className="flex w-full items-center justify-end gap-2">
            <Button
              variant="white"
              onClick={() => setResetConfirmOpen(false)}
              disabled={resetting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => void handleReset()}
              loading={resetting}
              disabled={resetting}
            >
              Reset
            </Button>
          </div>
        }
      >
        <p className="mb-0 text-sm text-gray-700">
          This deletes AL assessments, assignments, responses, disconnects, hub
          publish rows, customer levels, and related notifications for{" "}
          <strong>{selectedOrg?.name || "this organization"}</strong>. Accounts
          and users are not deleted.
        </p>
      </CommonModal>
    </div>
  );
};

export default AdminAlAssessmentTestContainer;
